import { useState, useEffect, useCallback } from "react";
import { FaPrint, FaTrash, FaMoneyBillWave, FaUserMd } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { HiXMark } from "react-icons/hi2";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import { PrintLayout } from "../../PrintLayout";
import DynamicSelect from "../../DynamicSelect";

const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const EmergencyAddPaymentModal = ({
  isOpen,
  onClose,
  bill: initialBill,
  onPaymentSuccess,
}) => {
  const [billDetails, setBillDetails] = useState(initialBill);
  const [transactions, setTransactions] = useState(initialBill?.payments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [doctors, setDoctors] = useState([]);

  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    paymentId: null,
  });
  const [printData, setPrintData] = useState(null);

  // Forms
  const [patientForm, setPatientForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    discount: "",
    paymentMode: "Cash",
    note: "",
  });

  const [doctorForm, setDoctorForm] = useState({
    doctorId: null,
    amount: "",
    note: "",
    date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (isOpen) {
      axios
        .get(`${BASE_URL}/doctor`, { headers: { "x-api-key": API_KEY } })
        .then((res) => {
          const rawData = Array.isArray(res.data)
            ? res.data
            : res.data.data || [];
          const options = rawData.map((d) => {
            let name = d.name || "Unknown";
            if (d.translations) {
              try {
                const t =
                  typeof d.translations === "string"
                    ? JSON.parse(d.translations)
                    : d.translations;
                name = t?.en?.name || t?.bn?.name || name;
              } catch (e) {}
            }
            return {
              value: d.id,
              label: name + (d.specialization ? ` (${d.specialization})` : ""),
            };
          });
          setDoctors(options);
        })
        .catch((err) => console.error("Error fetching doctors:", err));
    }
  }, [isOpen]);

  // 2. Fetch Latest Bill Data
  const fetchLatestData = useCallback(async () => {
    if (!initialBill?.id) return;
    try {
      const res = await axios.get(
        `${BASE_URL}/emergency/emergency-bills/${initialBill.id}`,
        {
          headers: { "x-api-key": API_KEY },
        },
      );
      const fullData = res.data.bill;
      console.log("Fetched Bill Data:", fullData);

      setBillDetails(fullData);
      setTransactions(fullData.payments || []);

      // Calculate Due
      const net = parseFloat(fullData.netAmount) || 0;
      const paid = parseFloat(fullData.paidAmount) || 0;
      const due = net - paid;

      setPatientForm((prev) => ({
        ...prev,
        amount: due > 0 ? due : "",
      }));
    } catch (error) {
      console.error("Error fetching bill data:", error);
    }
  }, [initialBill?.id]);

  useEffect(() => {
    if (isOpen && initialBill) {
      setBillDetails(initialBill);
      setTransactions(initialBill.payments || []);

      const net = parseFloat(initialBill.netAmount) || 0;
      const paid = parseFloat(initialBill.paidAmount) || 0;
      const due = net - paid;

      setPatientForm((prev) => ({
        ...prev,
        amount: due > 0 ? due : "",
      }));

      fetchLatestData();
    }
  }, [isOpen, initialBill, fetchLatestData]);

  const handlePatientSubmit = async (e) => {
    e.preventDefault();

    const remainingDue =
      (billDetails?.netAmount || 0) - (billDetails?.paidAmount || 0);
    if (
      parseFloat(patientForm.amount || 0) > remainingDue &&
      remainingDue > 0
    ) {
      toast.warning("Warning: Payment exceeds due amount.");
    }

    setIsSubmitting(true);
    try {
      const payload = {
        billId: billDetails.id,
        paymentDate: patientForm.date,
        amount: parseFloat(patientForm.amount || 0),
        discount: parseFloat(patientForm.discount || 0),
        paymentMode: patientForm.paymentMode,
        note: patientForm.note,
        doctorId: null,
        doctorAmount: null,
        doctorNote: null,
      };

      await axios.post(
        `${BASE_URL}/emergency/emergency-bills/add-payment`,
        payload,
        { headers: { "x-api-key": API_KEY } },
      );

      toast.success("Payment Received!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setPatientForm((prev) => ({
        ...prev,
        amount: "",
        discount: "",
        note: "",
      }));
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    if (!doctorForm.doctorId || !doctorForm.amount) {
      return toast.warning("Please select a doctor and amount.");
    }

    setIsSubmitting(true);
    try {
      const payload = {
        billId: billDetails.id,
        paymentDate: doctorForm.date,
        amount: 0,
        discount: 0,
        paymentMode: "Cash",
        note: "Doctor Payment",
        doctorId: doctorForm.doctorId,
        doctorAmount: parseFloat(doctorForm.amount),
        doctorNote: doctorForm.note,
      };

      await axios.post(
        `${BASE_URL}/emergency/emergency-bills/add-payment`,
        payload,
        { headers: { "x-api-key": API_KEY } },
      );

      toast.success("Doctor Paid!");
      await fetchLatestData();
      setDoctorForm((prev) => ({ ...prev, amount: "", note: "" }));
    } catch (error) {
      toast.error("Failed to pay doctor");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Print Logic
  useEffect(() => {
    if (printData) {
      const timer = setTimeout(() => {
        const printLayout = document.querySelector(".payment-history-print");
        if (printLayout) {
          printLayout.style.display = "block";
          window.print();
          setTimeout(() => {
            printLayout.style.display = "none";
            setPrintData(null);
          }, 500);
        } else {
          setPrintData(null);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [printData]);

  // Delete Transaction
  const initiateDelete = (paymentId) =>
    setConfirmDelete({ show: true, paymentId });

  const executeDelete = async () => {
    if (!confirmDelete.paymentId) return;
    try {
      await axios.delete(
        `${BASE_URL}/emergency/emergency-bills/delete-payment/${confirmDelete.paymentId}`,
        {
          headers: { "x-api-key": API_KEY },
        },
      );
      toast.success("Transaction Deleted!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setConfirmDelete({ show: false, paymentId: null });
    } catch (error) {
      toast.error("Failed to delete transaction.");
    }
  };

  const handlePrintReceipt = (tx) => {
    const isDoc = tx.doctorAmount > 0;
    setPrintData({
      title: isDoc
        ? "Emergency Doctor Payment Voucher"
        : "Emergency Payment Receipt",
      billInfo: (
        <>
          <div>
            <p>
              <strong>Receipt No:</strong> {tx.id ? `PAY-${tx.id}` : "N/A"}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {new Date(tx.paymentDate).toLocaleDateString("en-GB")}
            </p>
            <p>
              <strong>Payment Mode:</strong> {tx.paymentMode}
            </p>
          </div>
          <div>
            <p>
              <strong>Bill No:</strong> {billDetails.billNumber}
            </p>
            <p>
              <strong>Patient:</strong>{" "}
              {billDetails.patientName || billDetails.patient?.name}
            </p>
          </div>
        </>
      ),
      table: (
        <tbody>
          <tr>
            <td style={{ fontWeight: "bold", borderBottom: "1px solid #ddd" }}>
              Description
            </td>
            <td
              style={{
                textAlign: "right",
                fontWeight: "bold",
                borderBottom: "1px solid #ddd",
              }}
            >
              Amount
            </td>
          </tr>
          {isDoc ? (
            <tr>
              <td style={{ paddingTop: "8px" }}>
                Payment to Doctor:{" "}
                <strong>
                  {tx.doctor?.name
                    ? tx.doctor.translations &&
                      typeof tx.doctor.translations === "string"
                      ? JSON.parse(tx.doctor.translations)?.en?.name
                      : tx.doctor.name
                    : "Doctor"}
                </strong>
              </td>
              <td style={{ textAlign: "right", paddingTop: "8px" }}>
                ৳ {parseFloat(tx.doctorAmount).toFixed(2)}
              </td>
            </tr>
          ) : (
            <tr>
              <td style={{ paddingTop: "8px" }}>
                Payment Received ({tx.paymentMode})
              </td>
              <td style={{ textAlign: "right", paddingTop: "8px" }}>
                ৳ {parseFloat(tx.amount).toFixed(2)}
              </td>
            </tr>
          )}
          {(tx.note || tx.doctorNote) && (
            <tr>
              <td
                colSpan="2"
                style={{ fontSize: "0.9em", color: "#555", paddingTop: "4px" }}
              >
                Note: {isDoc ? tx.doctorNote : tx.note}
              </td>
            </tr>
          )}
        </tbody>
      ),
      summary: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontWeight: "bold",
            marginTop: "10px",
          }}
        >
          <span>Total Amount</span>
          <span>
            ৳ {parseFloat(isDoc ? tx.doctorAmount : tx.amount).toFixed(2)}
          </span>
        </div>
      ),
    });
  };

  if (!isOpen) return null;

  const activeBill = billDetails || initialBill || {};
  const patient = activeBill.patient || {};

  const currentTotal =
    activeBill.totalAmount ||
    activeBill.netAmount ||
    activeBill.billAmount ||
    0;
  const currentPaid = activeBill.paidAmount || 0;
  const currentDue = (activeBill.netAmount || currentTotal) - currentPaid;

  const InfoRow = ({ label, value, isBold, className = "" }) => (
    <div
      className={`flex justify-between border-b border-gray-100 py-2 text-sm last:border-0 ${className}`}
    >
      <span className="text-gray-500 font-medium">{label}</span>
      <span className={`text-right text-gray-800 ${isBold ? "font-bold" : ""}`}>
        {value || "-"}
      </span>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-4 bg-gray-200 rounded-t-lg border-b border-gray-300">
            <h2 className="text-lg font-bold">Manage Emergency Payments</h2>
            <button
              onClick={onClose}
              className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
            >
              <HiXMark />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* LEFT: Bill Details */}
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">
                    Bill Summary
                  </h3>
                  <InfoRow
                    label="Bill No"
                    value={activeBill.billNumber}
                    isBold
                  />
                  <InfoRow label="Total Bill" value={`৳ ${currentTotal}`} />
                  <InfoRow
                    label="Total Discount"
                    value={`৳ ${(activeBill.discount || 0).toLocaleString()}`}
                    className="text-blue-600"
                  />
                  <InfoRow
                    label="Total Paid"
                    value={`৳ ${currentPaid}`}
                    isBold
                  />
                  <div className="flex justify-between py-2 text-sm font-bold text-red-600 border-t mt-2">
                    <span>Due Amount</span>
                    <span>৳ {currentDue.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">
                    Patient Information
                  </h3>
                  <InfoRow
                    label="Patient Name"
                    value={activeBill.patientName || patient.name}
                  />
                  <InfoRow label="Phone" value={patient.phoneNumber} />
                  {patient.age && <InfoRow label="Age" value={patient.age} />}
                  {patient.gender && (
                    <InfoRow label="Gender" value={patient.gender} />
                  )}
                </div>
              </div>

              {/* RIGHT: FORMS & HISTORY */}
              <div className="lg:col-span-8 space-y-6">
                {/* 1. PATIENT PAYMENT (Green) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-[#73CB64]">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                    <FaMoneyBillWave className="text-[#73CB64]" /> Receive
                    Patient Payment
                  </h3>
                  <form onSubmit={handlePatientSubmit}>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Date
                        </label>
                        <input
                          type="date"
                          className="w-full border p-2 rounded text-sm"
                          value={patientForm.date}
                          onChange={(e) =>
                            setPatientForm({
                              ...patientForm,
                              date: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Amount (৳)
                        </label>
                        <input
                          type="number"
                          placeholder="0.00"
                          className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"
                          value={patientForm.amount}
                          onChange={(e) =>
                            setPatientForm({
                              ...patientForm,
                              amount: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Discount (৳)
                        </label>
                        <input
                          type="number"
                          placeholder="0.00"
                          className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"
                          value={patientForm.discount}
                          onChange={(e) =>
                            setPatientForm({
                              ...patientForm,
                              discount: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <label className="text-xs font-bold block mb-1">
                          Mode
                        </label>
                        <select
                          className="w-full border p-2 rounded text-sm"
                          value={patientForm.paymentMode}
                          onChange={(e) =>
                            setPatientForm({
                              ...patientForm,
                              paymentMode: e.target.value,
                            })
                          }
                        >
                          <option>Cash</option>
                          <option>Card</option>
                          <option>Mobile Banking</option>
                        </select>
                      </div>
                      <div className="flex-[2]">
                        <label className="text-xs font-bold block mb-1">
                          Note
                        </label>
                        <input
                          type="text"
                          className="w-full border p-2 rounded text-sm"
                          value={patientForm.note}
                          onChange={(e) =>
                            setPatientForm({
                              ...patientForm,
                              note: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-[#73CB64] text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition"
                      >
                        {isSubmitting ? "..." : "Receive Payment"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* 2. DOCTOR PAYMENT (Purple) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-M-primary-color/90">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                    <FaUserMd className="text-M-primary-color/90" /> Pay Doctor
                    (Commission/Fee)
                  </h3>
                  <form onSubmit={handleDoctorSubmit}>
                    <div className="mb-3">
                      <label className="text-xs font-bold block mb-1">
                        Select Doctor
                      </label>
                      <DynamicSelect
                        options={doctors}
                        value={doctors.find(
                          (d) => d.value === doctorForm.doctorId,
                        )}
                        onChange={(val) =>
                          setDoctorForm({ ...doctorForm, doctorId: val?.value })
                        }
                        placeholder="Search Doctor..."
                        isClearable
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Pay Amount (৳)
                        </label>
                        <input
                          type="number"
                          placeholder="0.00"
                          className="w-full border p-2 rounded text-sm focus:border-M-primary-color/90 outline-none"
                          value={doctorForm.amount}
                          onChange={(e) =>
                            setDoctorForm({
                              ...doctorForm,
                              amount: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Reason / Note
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Commission"
                          className="w-full border p-2 rounded text-sm"
                          value={doctorForm.note}
                          onChange={(e) =>
                            setDoctorForm({
                              ...doctorForm,
                              note: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting || !doctorForm.doctorId}
                        className="bg-M-primary-color/90 text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition disabled:opacity-50"
                      >
                        {isSubmitting ? "..." : "Pay Doctor"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* 3. TRANSACTION HISTORY */}
            <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-gray-800 font-bold border-b pb-3 mb-4 text-sm uppercase">
                Transaction History
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 font-semibold border-b">ID</th>
                      <th className="px-4 py-3 font-semibold border-b">Date</th>
                      <th className="px-4 py-3 font-semibold border-b">
                        Details
                      </th>
                      <th className="px-4 py-3 font-semibold border-b text-right">
                        Amount (৳)
                      </th>
                      <th className="px-4 py-3 font-semibold border-b text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-gray-700">
                    {transactions.length > 0 ? (
                      transactions.map((tx, i) => {
                        const isDoc = tx.doctorAmount > 0;
                        // Helper to safely get doctor name
                        let docName = "Doctor";
                        if (tx.doctor?.translations) {
                          try {
                            const t =
                              typeof tx.doctor.translations === "string"
                                ? JSON.parse(tx.doctor.translations)
                                : tx.doctor.translations;
                            docName = t?.en?.name || docName;
                          } catch (e) {}
                        } else if (tx.doctor?.name) {
                          docName = tx.doctor.name;
                        }

                        return (
                          <tr
                            key={i}
                            className={`hover:bg-gray-50 ${isDoc ? "bg-purple-50/40" : ""}`}
                          >
                            <td className="px-4 py-3 font-mono text-xs text-gray-500">
                              {tx.id ? `PAY-${tx.id}` : "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {new Date(tx.paymentDate).toLocaleDateString(
                                "en-GB",
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {isDoc ? (
                                <div className="flex flex-col">
                                  <span className="text-xs font-bold text-M-primary-color/90 flex items-center gap-1">
                                    <FaUserMd size={10} /> Paid to: {docName}
                                  </span>
                                  {tx.doctorNote && (
                                    <span className="text-[10px] text-gray-500 italic mt-0.5">
                                      {tx.doctorNote}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col">
                                  <span className="text-xs font-bold text-[#73CB64] flex items-center gap-1">
                                    <FaMoneyBillWave size={10} /> Received from
                                    Patient
                                  </span>
                                  <span className="text-[10px] text-gray-500 mt-0.5">
                                    Via {tx.paymentMode}{" "}
                                    {tx.note ? `- ${tx.note}` : ""}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-sm">
                              {isDoc ? (
                                <span className="text-red-500">
                                  - {parseFloat(tx.doctorAmount).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-[#73CB64]">
                                  + {parseFloat(tx.amount).toFixed(2)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center flex justify-center gap-2">
                              <button
                                onClick={() => handlePrintReceipt(tx)}
                                className="text-blue-500 hover:text-blue-700 transition"
                                title="Print"
                              >
                                <FaPrint />
                              </button>
                              <button
                                onClick={() => initiateDelete(tx.id)}
                                className="text-red-500 hover:text-red-700 transition"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-4 py-8 text-center text-gray-400 italic"
                        >
                          No transactions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Print Area */}
      {printData && (
        <PrintLayout
          className="payment-history-print"
          title={printData.title}
          billInfo={printData.billInfo}
          table={printData.table}
          summary={printData.summary}
          footer={printData.footer}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmActionModal
        isOpen={confirmDelete.show}
        onClose={() => setConfirmDelete({ show: false, paymentId: null })}
        onConfirm={executeDelete}
        ids={[confirmDelete.paymentId]}
        entityName="payment"
        title="Delete Payment"
        message="Are you sure? This will revert the bill balance."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default EmergencyAddPaymentModal;
